























import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { CvValue, EditorialProject } from '@/models'
import { Utils } from '@/utils'
import 'vis-timeline/styles/vis-timeline-graph2d.min.css'

type VisModule = typeof import('vis-timeline/standalone/esm/vis-timeline-graph2d.min')

interface TimelineItem {
  id: string;
  start: Date;
  end: Date;
  className: string;
  title: string;
  project: EditorialProject;
  ongoing: boolean;
  openEnded: boolean;
}

const DAY = 24 * 60 * 60 * 1000
// Statuses with their own colour; anything else uses the neutral one
const STATUS_KEYS = ['acti', 'prep', 'rvcr', 'noac']

const parseDay = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const addMonths = (date: Date, months: number): Date => new Date(date.getFullYear(), date.getMonth() + months, date.getDate())

/**
 * Production projects on a horizontal timeline that starts today: what is running now and what
 * comes next. Projects without a start date, or that ended before today, are not shown; those
 * without an end run until the last end date of the shown ones. Overlapping projects stack in
 * rows and the timeline packs a project in the first row with room for it.
 */
@Component
export default class EditorialProjectsTimeline extends Vue {
  @Prop({ required: true }) projects!: EditorialProject[]
  @Prop({ default: () => [] }) statuses!: CvValue[]

  // Not reactive on purpose (no initial value): Vue must not observe the vis-timeline objects
  private vis?: VisModule
  private timeline?: InstanceType<VisModule['Timeline']>

  get today(): Date {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }

  get locale(): string {
    return (this.$i18n.locale || 'es-ES').toString()
  }

  /**
   * Last day shown: the latest end date (at least three months ahead so the view is never
   * tiny), or a year from today when no project has an end date
   */
  get horizon(): Date {
    const ends = this.projects
      .filter(project => project.startDate && project.endDate)
      .map(project => parseDay(project.endDate as string).getTime() + DAY)
    if (!ends.length) return addMonths(this.today, 12)
    return new Date(Math.max(Math.max(...ends), addMonths(this.today, 3).getTime()))
  }

  get items(): TimelineItem[] {
    const today = this.today.getTime()
    return this.projects
      .filter(project => !!project.startDate)
      .filter(project => !project.endDate || parseDay(project.endDate).getTime() >= today)
      .map(project => {
        const realStart = parseDay(project.startDate as string)
        const openEnded = !project.endDate
        // The end date is inclusive, the timeline end exclusive
        const end = openEnded ? this.horizon : new Date(parseDay(project.endDate as string).getTime() + DAY)
        const ongoing = realStart.getTime() < today
        const status = (project.status || '').replace(/^epst-/, '')
        return {
          id: project.id,
          // The view is cut at today, so running projects start there and keep their label visible
          start: ongoing ? this.today : realStart,
          end,
          className: [
            'tl-item',
            'tl-status-' + (STATUS_KEYS.includes(status) ? status : 'other'),
            ongoing ? 'tl-ongoing' : '',
            openEnded ? 'tl-open' : ''
          ].join(' '),
          title: this.tooltip(project, openEnded),
          project,
          ongoing,
          openEnded
        }
      })
  }

  get legends(): { key: string; name: string }[] {
    return STATUS_KEYS
      .map(key => {
        const status = this.statuses.find(item => item.codeId === 'epst-' + key)
        return { key, name: (status && status.name) || key }
      })
  }

  private formatDay(value: string): string {
    return new Intl.DateTimeFormat(this.locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(parseDay(value))
  }

  private period(project: EditorialProject, openEnded: boolean): string {
    const start = this.formatDay(project.startDate as string)
    const end = openEnded ? this.$t('editorial_project_view.timeline.no_end') : this.formatDay(project.endDate as string)
    return `${start} → ${end}`
  }

  private tooltip(project: EditorialProject, openEnded: boolean): string {
    const status = this.statuses.find(item => item.codeId === project.status)
    return [project.name, this.period(project, openEnded), status && status.name].filter(text => text).join(' · ')
  }

  /** Bar content built as elements (no HTML strings): flag + name, and the period below */
  private renderItem(item: TimelineItem): HTMLElement {
    const project = item.project
    const root = document.createElement('div')
    root.className = 'tl-content'

    const title = document.createElement('div')
    title.className = 'tl-title'
    if (project.country) {
      const flag = document.createElement('span')
      flag.className = 'fi tl-flag ' + Utils.getCountryIcon(project.country)
      title.appendChild(flag)
    }
    const name = document.createElement('span')
    name.textContent = project.name
    title.appendChild(name)

    const dates = document.createElement('div')
    dates.className = 'tl-dates'
    dates.textContent = this.period(project, item.openEnded)

    root.appendChild(title)
    root.appendChild(dates)
    return root
  }

  private options() {
    const monthFormat = new Intl.DateTimeFormat(this.locale, { month: 'short' })
    const yearFormat = new Intl.DateTimeFormat(this.locale, { year: 'numeric' })
    const asDate = (date: { toDate?: () => Date } | Date) => ((date as { toDate?: () => Date }).toDate ? (date as { toDate: () => Date }).toDate() : date as Date)
    return {
      min: this.today,
      max: addMonths(this.horizon, 1),
      start: this.today,
      end: new Date(Math.min(addMonths(this.today, 12).getTime(), addMonths(this.horizon, 1).getTime())),
      zoomMin: 60 * DAY,
      orientation: { axis: 'top', item: 'top' },
      stack: true,
      order: (a: TimelineItem, b: TimelineItem) => a.start.getTime() - b.start.getTime(),
      margin: { item: { horizontal: 6, vertical: 8 }, axis: 10 },
      maxHeight: '65vh',
      verticalScroll: true,
      horizontalScroll: true,
      zoomKey: 'ctrlKey',
      selectable: false,
      showCurrentTime: false,
      timeAxis: { scale: 'month', step: 1 },
      format: {
        minorLabels: (date: Date) => monthFormat.format(asDate(date)),
        majorLabels: (date: Date) => yearFormat.format(asDate(date))
      },
      template: (item: TimelineItem) => this.renderItem(item),
      tooltip: { followMouse: true, overflowMethod: 'cap' }
    }
  }

  async mounted() {
    // Loaded on demand like exceljs: only who opens the calendar downloads it
    this.vis = await import(/* webpackChunkName: "vis-timeline" */ 'vis-timeline/standalone/esm/vis-timeline-graph2d.min')
    this.drawTimeline()
  }

  beforeDestroy() {
    if (this.timeline) this.timeline.destroy()
  }

  @Watch('items')
  onItemsChanged() {
    this.drawTimeline()
  }

  // Not called `render`: in class components that name is the component render function
  private drawTimeline() {
    if (!this.vis) return
    const data = new this.vis.DataSet(this.items as never[])
    if (!this.timeline) {
      const container = this.$refs.timeline as HTMLElement
      this.timeline = new this.vis.Timeline(container, data, this.options() as never)
      this.timeline.on('click', (properties: { item: string | null }) => {
        const item = this.items.find(entry => entry.id === properties.item)
        if (item) this.$emit('select', item.project)
      })
    } else {
      this.timeline.setOptions(this.options() as never)
      this.timeline.setItems(data)
    }
  }
}
